import { dashboard } from "@/lib/api";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  businessActivityEvents,
  deriveActionItems,
  deriveBudgetPositions,
} from "@/lib/product-intelligence.mjs";
import { useData, matches, type View, type ViewProps } from "@/lib/app-view";
import type { AppRole } from "@/lib/types";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Loading, ErrorState } from "@/components/empty-state";
import { AppLink } from "@/components/app-link";

type DashboardPermissions = {
  requisition?: boolean;
  approve: boolean;
  procure: boolean;
  receive: boolean;
  finance: boolean;
  manage: boolean;
};

const roleCopy: Record<AppRole, { label: string; description: string }> = {
  requester: {
    label: "Requester command centre",
    description: "Complete requests, answer returned work and follow progress without chasing another team.",
  },
  approver: {
    label: "Approver command centre",
    description: "Decide assigned requests with the budget position and accountable evidence in view.",
  },
  procurement: {
    label: "Procurement command centre",
    description: "Move approved demand through sourcing, award and purchase-order issue.",
  },
  receiver: {
    label: "Receiving command centre",
    description: "Record what arrived and surface shortages, damage or outstanding quantities.",
  },
  finance: {
    label: "Finance command centre",
    description: "Control budget warnings, invoice exceptions, payment evidence and reconciliation.",
  },
  tenant_admin: {
    label: "Organisation command centre",
    description: "See work requiring attention across roles, budgets and the purchase journey.",
  },
  auditor: {
    label: "Assurance command centre",
    description: "Review exceptions and trace current activity back to governed evidence.",
  },
  platform_owner: {
    label: "Platform command centre",
    description: "Review tenant activity and operational controls without changing source evidence.",
  },
};

export function Dashboard({
  organisation,
  refresh,
  query,
  can,
  canRequest,
  currentUserId,
  role,
  permissions,
  navigate,
}: Pick<ViewProps, "organisation" | "refresh" | "query" | "can"> & {
  canRequest: boolean;
  currentUserId: string;
  role: AppRole;
  permissions: DashboardPermissions;
  navigate: (view: View, recordId?: string) => void;
}) {
  const { data, error, loading } = useData(
    () => dashboard(organisation.id),
    [organisation.id, refresh],
  );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;

  const activity = businessActivityEvents(data.audit)
    .filter((event) =>
      matches(
        query,
        event.display_action,
        event.entity_type,
        JSON.stringify(event.metadata),
      ),
    )
    .slice(0, 4);
  const actions = deriveActionItems(data, permissions, currentUserId).filter(
    (item) => matches(query, item.reference, item.title, item.detail),
  );
  const budgetPositions = deriveBudgetPositions(data.budgets || []);
  const ownOpenRequests = data.requisitions.filter(
    (row) =>
      row.created_by === currentUserId &&
      !["rejected", "sourced"].includes(row.status),
  ).length;
  const approvedToSource = data.requisitions.filter(
    (row) => row.status === "approved",
  ).length;
  const draftOrders = data.orders.filter((row) => row.status === "draft").length;
  const roleMetrics = metricsForRole(role, {
    ownOpenRequests,
    actionCount: actions.length,
    approvedToSource,
    draftOrders,
    pendingApprovals: data.metrics.pendingApprovals,
    activeRfqs: data.metrics.activeRfqs,
    openOrders: data.metrics.openOrders,
    invoiceExceptions: data.metrics.invoiceExceptions,
    budgetWarnings: data.metrics.budgetWarnings,
    committedSpend: data.metrics.committedSpend,
    recentEvents: data.audit.length,
  });
  const showBudgets =
    permissions.approve || permissions.finance || permissions.manage;
  const copy = roleCopy[role];

  return (
    <>
      <PageHeader
        eyebrow="MY RAQOZ WORKSPACE"
        title={copy.label}
        description={`${organisation.name} · ${copy.description}`}
      />
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {roleMetrics.map(([label, value, note, destination]) => (
          <AppLink
            key={label}
            view={destination}
            navigate={navigate}
            ariaLabel={`Open ${label.toLowerCase()}`}
            className="group block rounded-xl"
          >
            <div className="surface h-full p-5 transition-[border-color,background-color,box-shadow] group-hover:border-[var(--action-border)] group-hover:bg-[var(--action-soft)] group-focus-visible:border-[var(--action-border)]">
              <span className="text-xs text-[var(--text-secondary)]">{label}</span>
              <strong className="my-3 block text-3xl font-semibold tabular-nums">
                {value}
              </strong>
              <small className="text-[var(--text-secondary)]">{note}</small>
            </div>
          </AppLink>
        ))}
      </section>

      <section
        className={`mt-4 grid gap-4 ${
          showBudgets
            ? "xl:grid-cols-[1.35fr_.85fr]"
            : "xl:grid-cols-[1.45fr_.75fr]"
        }`}
      >
        <Panel>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="eyebrow">ROLE-ASSIGNED WORK</p>
              <h2 className="mt-1 text-xl font-semibold">My work</h2>
            </div>
            <Button variant="ghost" size="sm" className="min-h-11" asChild>
              <AppLink view="actions" navigate={navigate}>All actions</AppLink>
            </Button>
          </div>
          {actions.length ? (
            actions.slice(0, 6).map((item) => (
              <AppLink
                className="grid min-h-11 w-full grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-[var(--border)] py-3 text-left last:border-0 hover:text-[var(--action-hover)]"
                key={item.id}
                view={actionView(item.entityType, item.title)}
                recordId={item.entityId}
                navigate={navigate}
              >
                <span>
                  <strong className="block text-sm">{item.title}</strong>
                  <small className="mt-1 block text-[var(--text-secondary)]">
                    {item.reference} · {item.detail}
                  </small>
                  {item.budget && (
                    <small className="mt-1 block text-[var(--warning)]">
                      {item.budget.category}:{" "}
                      {money(item.budget.consumed, item.budget.currency)} of{" "}
                      {money(item.budget.allocated, item.budget.currency)}{" "}
                      reserved or committed
                    </small>
                  )}
                </span>
                <small className="text-[var(--text-secondary)]">
                  {item.dueAt ? displayDate(item.dueAt) : "Open"}
                </small>
              </AppLink>
            ))
          ) : (
            <p className="rounded-xl bg-[var(--surface-secondary)] p-4 text-sm text-[var(--text-secondary)]">
              No assigned workflow action currently requires your attention.
            </p>
          )}
        </Panel>

        {showBudgets ? (
          <Panel>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="eyebrow">FINANCIAL CONTEXT</p>
                <h2 className="mt-1 text-xl font-semibold">Budget position</h2>
              </div>
              <Button variant="ghost" size="sm" className="min-h-11" asChild>
                <AppLink view="budgets" navigate={navigate}>Open budgets</AppLink>
              </Button>
            </div>
            <div className="grid gap-4">
              {budgetPositions.slice(0, 3).map((position) => (
                <div key={position.currency}>
                  <div className="flex items-end justify-between gap-3 text-sm tabular-nums">
                    <span className="min-w-0">
                      <strong>
                        {money(position.consumed, position.currency)}
                      </strong>
                      <small className="block text-[var(--text-secondary)]">
                        reserved or committed
                      </small>
                    </span>
                    <span className="min-w-0 text-right">
                      <strong>
                        {money(position.remaining, position.currency)}
                      </strong>
                      <small className="block text-[var(--text-secondary)]">remaining</small>
                    </span>
                  </div>
                  <div
                    className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--surface-secondary)]"
                    role="progressbar"
                    aria-label={`${position.currency} budget consumed`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.min(100, position.consumedPercent)}
                  >
                    <div
                      className={`h-full ${
                        position.consumedPercent >= 100
                          ? "bg-[var(--error)]"
                          : position.consumedPercent >= 80
                            ? "bg-[var(--warning)]"
                            : "bg-[var(--success)]"
                      }`}
                      style={{
                        width: `${Math.min(100, position.consumedPercent)}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
              {!budgetPositions.length && (
                <p className="text-sm text-[var(--text-secondary)]">
                  No active budget allocation is recorded.
                </p>
              )}
            </div>
          </Panel>
        ) : (
          <Panel>
            <h2 className="mb-3 text-xl font-semibold">Quick actions</h2>
            <div className="grid gap-2">
              {canRequest && (
                <Quick
                  title="New requisition"
                  note="Specify need and budget"
                  view="requisitions"
                  navigate={navigate}
                />
              )}
              {can("approve") && (
                <Quick
                  title="Review approvals"
                  note="Decide assigned requests"
                  view="approvals"
                  navigate={navigate}
                />
              )}
              {can("procure") && (
                <Quick
                  title="Manage sourcing"
                  note="RFQs, quotes and awards"
                  view="rfqs"
                  navigate={navigate}
                />
              )}
              {can("receive") && (
                <Quick
                  title="Receive goods"
                  note="Post accepted quantities"
                  view="orders"
                  navigate={navigate}
                />
              )}
            </div>
          </Panel>
        )}
      </section>

      <Panel className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <p className="eyebrow">SECONDARY CONTEXT</p>
            <h2 className="mt-1 text-lg font-semibold">Recent activity</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="min-h-11"
            onClick={() => navigate("audit")}
          >
            Full audit trail
          </Button>
        </div>
        {activity.length ? (
          <div className="grid gap-x-6 md:grid-cols-2">
            {activity.map((event) => (
              <div
                key={event.id}
                className="grid grid-cols-[8px_minmax(0,1fr)] gap-3 border-t border-[var(--border)] py-3 sm:grid-cols-[8px_minmax(0,1fr)_auto]"
              >
                <i
                  className="mt-1 size-2 rounded-full bg-[var(--action)]"
                  aria-hidden="true"
                />
                <span>
                  <strong className="block text-sm">
                    {titleCase(String(event.display_action))}
                  </strong>
                  <small className="text-[var(--text-secondary)]">
                    {titleCase(event.entity_type)}
                  </small>
                </span>
                <small className="col-start-2 text-[var(--text-secondary)] tabular-nums sm:col-start-auto">
                  {displayDate(event.created_at, true)}
                </small>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--text-secondary)]">
            Activity appears as governed work moves through RAQOZ.
          </p>
        )}
      </Panel>
    </>
  );
}

function metricsForRole(
  role: AppRole,
  value: Record<string, number>,
): [string, string | number, string, View][] {
  const committed = money(value.committedSpend);
  const catalog: Record<AppRole, [string, string | number, string, View][]> = {
    requester: [
      ["My open requests", value.ownOpenRequests, "Draft through approval", "requisitions"],
      ["Needs my action", value.actionCount, "Complete or revise", "actions"],
      ["Active RFQs", value.activeRfqs, "Organisation sourcing", "rfqs"],
      ["Open orders", value.openOrders, "Awaiting receipt", "orders"],
    ],
    approver: [
      ["Assigned to me", value.actionCount, "Awaiting my decision", "actions"],
      ["Pending approvals", value.pendingApprovals, "Organisation-wide", "approvals"],
      ["Budget warnings", value.budgetWarnings, "Need accountable review", "budgets"],
      ["Committed spend", committed, "Issued purchase orders", "orders"],
    ],
    procurement: [
      ["Needs my action", value.actionCount, "Sourcing and PO work", "actions"],
      ["Approved to source", value.approvedToSource, "Ready for procurement", "requisitions"],
      ["Active RFQs", value.activeRfqs, "Collecting responses", "rfqs"],
      ["Draft purchase orders", value.draftOrders, "Awaiting issue", "orders"],
    ],
    receiver: [
      ["Needs receipt action", value.actionCount, "Assigned receiving work", "actions"],
      ["Open orders", value.openOrders, "Issued or part-received", "orders"],
      ["Match exceptions", value.invoiceExceptions, "Downstream discrepancies", "invoices"],
      ["Recent events", value.recentEvents, "Recorded control activity", "audit"],
    ],
    finance: [
      ["Needs my action", value.actionCount, "Match and payment work", "actions"],
      ["Match exceptions", value.invoiceExceptions, "Require resolution", "invoices"],
      ["Budget warnings", value.budgetWarnings, "Need accountable review", "budgets"],
      ["Committed spend", committed, "Issued purchase orders", "orders"],
    ],
    tenant_admin: [
      ["Needs action", value.actionCount, "Across your permissions", "actions"],
      ["Pending approvals", value.pendingApprovals, "Awaiting decision", "approvals"],
      ["Budget warnings", value.budgetWarnings, "Need accountable review", "budgets"],
      ["Committed spend", committed, "Issued purchase orders", "orders"],
    ],
    auditor: [
      ["Match exceptions", value.invoiceExceptions, "Recorded exceptions", "invoices"],
      ["Budget warnings", value.budgetWarnings, "Open control signals", "budgets"],
      ["Open orders", value.openOrders, "Outstanding fulfilment", "orders"],
      ["Recent events", value.recentEvents, "Traceable activity", "audit"],
    ],
    platform_owner: [
      ["Needs action", value.actionCount, "Across platform permissions", "actions"],
      ["Pending approvals", value.pendingApprovals, "Tenant workflow", "approvals"],
      ["Budget warnings", value.budgetWarnings, "Open control signals", "budgets"],
      ["Committed spend", committed, "Recorded purchase orders", "orders"],
    ],
  };
  return catalog[role];
}

function actionView(entityType: string, title: string): View {
  if (entityType === "rfq") return "rfqs";
  if (entityType === "purchase_order") return "orders";
  if (entityType === "invoice") return "invoices";
  if (entityType === "requisition" && title.startsWith("Approve"))
    return "approvals";
  return "requisitions";
}

function Quick({
  title,
  note,
  view,
  navigate,
}: {
  title: string;
  note: string;
  view: View;
  navigate: (view: View, recordId?: string) => void;
}) {
  return (
    <AppLink
      view={view}
      navigate={navigate}
      className="min-h-11 rounded-xl border border-[var(--border)] p-4 text-left hover:border-[var(--action-border)] hover:bg-[var(--action-soft)]"
    >
      <strong className="block text-sm">{title}</strong>
      <small className="mt-1 block text-[var(--text-secondary)]">{note}</small>
    </AppLink>
  );
}
